// CRM Call Journaling (write-back) — 3CX parity: log every completed call into
// the connected CRM as an activity/call record. Settings + recent write-back log.
// Backend: /api/crm-writeback (routes/crm_writeback.js, worker lib/crm_writeback.js).

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, RefreshCw, FlaskConical } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from '../components/Toast';

const auth = () => ({ Authorization: `Bearer ${getToken()}` });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...auth(), ...(init.headers || {}) },
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

const TEMPLATES = ['auto', 'hubspot', 'salesforce', 'zoho', 'dynamics', 'generic', 'custom'];

const STATUS_STYLE: Record<string, string> = {
  sent:    'bg-green-500/15 text-green-400',
  pending: 'bg-yellow-500/15 text-yellow-400',
  failed:  'bg-red-500/15 text-red-400',
  skipped: 'bg-gray-500/15 text-textSecondary',
};

export default function CrmWriteback() {
  const qc = useQueryClient();
  const toast = useToast();

  const cfg = useQuery<any>({ queryKey: ['crm-writeback'], queryFn: () => fJson('/api/crm-writeback') });
  const log = useQuery<any>({
    queryKey: ['crm-writeback-log'],
    queryFn: () => fJson('/api/crm-writeback/log'),
    refetchInterval: 15_000,
  });

  const [enabled, setEnabled] = useState(false);
  const [journalInternal, setJournalInternal] = useState(false);
  const [answeredOnly, setAnsweredOnly] = useState(true);
  const [minDuration, setMinDuration] = useState(0);
  const [requireMatch, setRequireMatch] = useState(false);
  const [template, setTemplate] = useState('auto');
  const [recordingBase, setRecordingBase] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [customMethod, setCustomMethod] = useState('POST');
  const [customBody, setCustomBody] = useState('');
  const [customAuthType, setCustomAuthType] = useState('none');
  const [customAuthValue, setCustomAuthValue] = useState('');
  const [testResult, setTestResult] = useState<any>(null);

  useEffect(() => {
    const c = cfg.data;
    if (!c) return;
    setEnabled(!!c.enabled);
    setJournalInternal(!!c.journal_internal);
    setAnsweredOnly(c.answered_only !== false);
    setMinDuration(c.min_duration_sec || 0);
    setRequireMatch(!!c.require_match);
    setTemplate(c.template || 'auto');
    setRecordingBase(c.recording_base_url || '');
    const ct = c.custom_template || {};
    setCustomUrl(ct.url || '');
    setCustomMethod(ct.method || 'POST');
    setCustomBody(ct.body || '');
    setCustomAuthType(ct.auth?.type || 'none');
    setCustomAuthValue(ct.auth?.value || '');
  }, [cfg.data]);

  const buildSettings = () => ({
    enabled,
    journal_internal: journalInternal,
    answered_only: answeredOnly,
    min_duration_sec: minDuration,
    require_match: requireMatch,
    template,
    recording_base_url: recordingBase,
    custom_template: template === 'custom' ? {
      url: customUrl, method: customMethod, body: customBody,
      auth: customAuthType === 'none' ? null : { type: customAuthType, value: customAuthValue },
    } : null,
  });

  const save = useMutation({
    mutationFn: () => fJson('/api/crm-writeback', { method: 'PUT', body: JSON.stringify(buildSettings()) }),
    onSuccess: () => { toast('success', 'Saved'); qc.invalidateQueries({ queryKey: ['crm-writeback'] }); },
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  const runTest = useMutation({
    mutationFn: () => fJson('/api/crm-writeback/test', {
      method: 'POST',
      body: JSON.stringify({ settings: buildSettings() }),
    }),
    onSuccess: (r: any) => setTestResult(r),
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  const retry = useMutation({
    mutationFn: (id: number) => fJson(`/api/crm-writeback/retry/${id}`, { method: 'POST' }),
    onSuccess: () => { toast('success', 'Requeued'); qc.invalidateQueries({ queryKey: ['crm-writeback-log'] }); },
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  if (cfg.isLoading) return <div>Loading…</div>;
  const summary = log.data?.summary || {};

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold flex-1">CRM Call Journaling</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => runTest.mutate()} disabled={runTest.isPending}>
          <FlaskConical size={14} /> Test Render
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => save.mutate()} disabled={save.isPending}>
          <Save size={14} /> Save
        </button>
      </div>
      <p className="text-sm text-textSecondary">
        Log completed calls into the connected CRM as call activities — contact matched by phone number,
        with direction, duration, agent and a recording link. CRM connection and credentials come from
        Integrations → CRM; template 'auto' follows the configured vendor{cfg.data?.vendor ? ` (currently: ${cfg.data.vendor})` : ''}.
      </p>

      <div className="grid grid-cols-2 gap-4">
        <div className="card p-4 space-y-3">
          <h2 className="font-semibold">Journaling</h2>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            Enable CRM call journaling
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={answeredOnly} onChange={e => setAnsweredOnly(e.target.checked)} />
            Answered calls only
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={journalInternal} onChange={e => setJournalInternal(e.target.checked)} />
            Also journal internal (extension-to-extension) calls
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={requireMatch} onChange={e => setRequireMatch(e.target.checked)} />
            Only journal when the number matches a CRM contact
          </label>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Minimum duration (seconds, 0 = no minimum)</label>
            <input className="input w-32" type="number" min={0} value={minDuration}
              onChange={e => setMinDuration(Math.max(0, parseInt(e.target.value, 10) || 0))} />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Recording link base URL (e.g. https://pbx.example.com)</label>
            <input className="input" value={recordingBase} onChange={e => setRecordingBase(e.target.value)}
              placeholder="https://pbx.example.com" />
          </div>
        </div>

        <div className="card p-4 space-y-3">
          <h2 className="font-semibold">Write template</h2>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Template</label>
            <select className="input" value={template} onChange={e => setTemplate(e.target.value)}>
              {TEMPLATES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {template === 'custom' && (
            <>
              <div>
                <label className="block text-sm text-textSecondary mb-1">URL</label>
                <input className="input font-mono text-xs" value={customUrl} onChange={e => setCustomUrl(e.target.value)}
                  placeholder="https://crm.example.com/api/activities" />
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">Method</label>
                <select className="input w-32" value={customMethod} onChange={e => setCustomMethod(e.target.value)}>
                  {['POST', 'PUT', 'PATCH'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  Body — placeholders: {'{number} {name} {direction} {duration} {agent_ext} {agent_name} {start_time} {disposition} {recording_url}'}
                </label>
                <textarea className="input font-mono text-xs h-28" value={customBody} onChange={e => setCustomBody(e.target.value)}
                  placeholder={'{"phone":"{number}","note":"Call {direction} {duration}s with {name}"}'} />
              </div>
              <div className="flex gap-2">
                <select className="input w-32" value={customAuthType} onChange={e => setCustomAuthType(e.target.value)}>
                  {['none', 'bearer', 'basic', 'apikey'].map(t => <option key={t}>{t}</option>)}
                </select>
                {customAuthType !== 'none' && (
                  <input className="input flex-1 font-mono text-xs" type="password" value={customAuthValue}
                    onChange={e => setCustomAuthValue(e.target.value)} placeholder="auth value" />
                )}
              </div>
            </>
          )}
          {testResult && (
            <pre className="text-xs font-mono bg-bg p-3 rounded overflow-auto max-h-64">{JSON.stringify(testResult, null, 2)}</pre>
          )}
        </div>
      </div>

      <div className="card p-4 space-y-3">
        <div className="flex items-center gap-3">
          <h2 className="font-semibold flex-1">Recent write-backs</h2>
          <span className="text-xs text-textSecondary">
            sent {summary.sent || 0} · pending {summary.pending || 0} · failed {summary.failed || 0} · skipped {summary.skipped || 0}
          </span>
          <button className="btn-primary text-sm inline-flex items-center gap-1"
            onClick={() => qc.invalidateQueries({ queryKey: ['crm-writeback-log'] })}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-textSecondary">
                <th className="py-1 pr-3">When</th>
                <th className="py-1 pr-3">Number</th>
                <th className="py-1 pr-3">Name</th>
                <th className="py-1 pr-3">Dir</th>
                <th className="py-1 pr-3">Dur</th>
                <th className="py-1 pr-3">Agent</th>
                <th className="py-1 pr-3">Status</th>
                <th className="py-1 pr-3">Detail</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {(log.data?.entries || []).map((e: any) => {
                const c = e.call_ctx || {};
                return (
                  <tr key={e.id} className="border-t border-border/40">
                    <td className="py-1 pr-3 whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</td>
                    <td className="py-1 pr-3 font-mono text-xs">{c.number || ''}</td>
                    <td className="py-1 pr-3">{c.name || ''}</td>
                    <td className="py-1 pr-3">{c.direction || ''}</td>
                    <td className="py-1 pr-3">{c.duration ? `${c.duration}s` : ''}</td>
                    <td className="py-1 pr-3">{c.agent_name || c.agent_ext || ''}</td>
                    <td className="py-1 pr-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${STATUS_STYLE[e.status] || ''}`}>{e.status}</span>
                    </td>
                    <td className="py-1 pr-3 text-xs text-textSecondary max-w-[26rem] truncate"
                        title={e.last_error || e.skip_reason || ''}>
                      {e.status === 'sent' ? `HTTP ${e.response_code}` : (e.last_error || e.skip_reason || '')}
                      {e.status === 'pending' && e.attempts > 0 ? ` (attempt ${e.attempts})` : ''}
                    </td>
                    <td className="py-1 text-right">
                      {(e.status === 'failed' || e.status === 'skipped') && (
                        <button className="text-xs underline text-textSecondary" onClick={() => retry.mutate(e.id)}>retry</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!log.data?.entries?.length && (
                <tr><td colSpan={9} className="py-3 text-center text-textSecondary">No write-backs yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
